// English static UI texts. Keys must mirror locales/pl.js exactly
// (enforced by src/i18n/__tests__/i18n.test.js).
// Terminology follows docs/translation-glossary.md (migration_slownik.docx, column "Propozycja").
//
// Backend-supplied text (hotel and tour names, people's names, server error messages) is NOT
// translated here - it renders exactly as delivered by the API.

export const en = {
   common: {
      appTitle: "Rep Panel — Task schedule",
      warning: "Warning!",
      // react-select renders its own English "Select..." when no placeholder is given.
      selectDestination: "Select destination",
      selectDay: "Select day",
      language: "Language",
      actions: {
         cancel: "Cancel",
         saveChanges: "Save changes",
         back: "Back",
         ok: "Ok",
         yes: "Yes",
         no: "No",
         yesUpper: "YES",
         noUpper: "NO"
      }
   },

   menu: {
      dataSynced: "Data synced",
      logout: "Log out",
      logoutConfirm: "Are you sure you want to log out?"
   },

   auth: {
      accessDeniedTitle: "Access denied",
      accessDeniedMessage: "You do not have permission to view this page.",
      notFoundTitle: "404 - Page not found",
      notFoundMessage: "The page you are looking for does not exist."
   },

   scheduler: {
      title: "Task schedule",
      noDestinations: "No tour destinations",
      selectAll: "Select all",
      animatorNamePlaceholder: "Full name",
      clearSchedule: "Clear schedule",
      copyToNextWeek: "Copy to next week",
      confirmAndCopy: "Confirm and copy",
      confirm: {
         removeUser: "Are you sure you want to remove this person from the schedule?",
         clearSchedule: "Are you sure you want to clear the schedule?"
      }
   },

   copySchedule: {
      overwriteHeader: "Copying with overwrite",
      confirmContinue: "Yes, continue",
      conflictIntro: "Users with booked slots in the following week:<br>",
      conflictWarning: "<br><br>Continuing will overwrite the existing tasks for these users.",
      unknownUser: "User ID: {id}"
   },

   tasks: {
      addTask: "Add task",
      confirmDelete: "Are you sure you want to delete this task?",
      types: {
         dyzurTelefoniczny: "On-call duty",
         spotkanieInformacyjne: "Welcome meeting",
         wizytaHotelowa: "Hotel visit",
         wycieczkaFakultatywna: "Optional excursion",
         biuro: "Office",
         dayOff: "DAY OFF",
         inne: "Other",
         transfer: "Transfer",
         wylot: "Departure",
         przylot: "Arrival",
         animacja: "Entertainment"
      },
      validation: {
         missingDataOneHotel: "Fill in all form fields. Data is missing for hotel {hotels}.",
         missingDataManyHotels: "Fill in all form fields. Data is missing for hotels {hotels}."
      }
   },

   taskForms: {
      selectTask: "Select task",
      taskName: "Task name",
      meetingPlace: "Meeting place",
      time: "Time",
      timeFrom: "Time from",
      timeTo: "Time to",
      assigned: "Assigned:",
      addPerson: "Add person",
      selectedHotels: "Selected hotels:",
      addHotel: "Add hotel",
      notes: "Remarks",
      selectTrip: "Select tour",
      selectRoute: "Select route",
      selectTransfer: "Select transfer",
      meetingTime: "Pick-up time",
      meetingPoint: "Pick-up point",
      clientTransfers: "Client transfers",
      showClientTransfers: "Show client transfers",
      hideClientTransfers: "Hide client transfers",
      selectedHotelsCount: "Number of selected hotels:",
      showOnlySelectedHotels: "Show only selected hotels",
      noResultsFor: "No results for {query}"
   },

   animations: {
      title: "Entertainment schedule",
      addAnimation: "Add entertainment"
   },

   prints: {
      title: "PDF documents",
      savePdf: "Save PDF",
      saveAndPrint: "Save and print",
      lastGeneratedPrefix: "The PDF was last generated on",
      lastGeneratedSuffix: ".",
      types: {
         flight: "Departure",
         welcome: "Welcome letter",
         welcomeEnvelope: "Welcome envelope"
      },
      themes: {
         color: "Colour",
         blackAndWhite: "Black and white"
      },
      placeholders: {
         topNotes: "Remarks above the table",
         bottomNotes: "Remarks below the table",
         resident: "Representative",
         emergencyNumber: "Enter the emergency number",
         welcomeInfo: "Welcome information",
         meetingsComment: "Comment on meetings and on-call duty",
         selectDestination: "Select destination",
         date: "Date",
         selectPdf: "Select PDF",
         selectFormat: "Select format",
         selectType: "Select type",
         selectHotel: "Select hotel"
      },
      errors: {
         noPrintType: "No document type selected",
         noHotel: "No hotel selected",
         noResident: "No representative selected",
         noTransfers: "No transfers to generate for the given data"
      }
   },

   report: {
      openButton: "Generate report",
      dialogTitle: "Report generation",
      selectParams: "Select the report parameters:",
      dateRange: "Date range:",
      users: "Users:",
      taskType: "Task type:",
      generate: "Generate",
      generating: "Generating...",
      placeholders: {
         dateRange: "Select a date range",
         users: "Select users",
         taskTypes: "Select task types"
      }
   },

   toasts: {
      animatorNameTooShort: "The name must be at least 3 characters long!",
      scheduleCleared: "The schedule has been cleared",
      scheduleCopied: "The schedule has been copied to the next week",
      copyVerificationError: "Error while verifying the copy",
      copyError: "Error while copying the schedule",
      fillAllForms: "Fill in all form fields!",
      // Scaffolding around the handleApiError message. {message} is backend text and renders
      // exactly as delivered - only the frame around it is translated.
      apiError: "Error {status}: {message}",
      apiErrorNoStatus: "Error: {message}",
      unknownError: "An unknown error occurred",
      errorParseFailed: "Failed to read the error message"
   },

   pdf: {
      welcome: {
         headerBadge: "A warm welcome",
         resident: "Representative",
         medicalAssistance: "Medical assistance",
         emergencyPhone: "Emergency phone",
         meetings: "Meetings",
         infoMeeting: "Welcome meeting",
         hotelVisit: "Hotel visit",
         phoneDuty: "On-call duty",
         date: "Date",
         time: "Time",
         place: "Place",
         from: "From",
         to: "To",
         phoneContact: "Phone contact",
         insurance:
            "Your tour includes insurance provided by Towarzystwo Ubezpieczeń EUROPA S.A.",
         policyIntro: "The group policy number for all Rainbow Tours S.A. clients is ",
         lifeThreatening: "If the situation is life-threatening for the Tourist, ",
         lifeThreateningBold: "call an ambulance immediately!",
         nonLifeThreatening:
            "If the situation is not life-threatening but medical assistance is advisable, please first contact the ",
         helpCentreBold: "HELP CENTRE, open 24 hours a day: +48 22 568 98 28",
         polishSpeaking:
            "Polish-speaking consultants will arrange a doctor's appointment for you.",
         noticeText:
            " Every Tourist using medical assistance under the TU EUROPA insurance policy is required to contact the HELP CENTRE. After contacting the HELP CENTRE, please call the Rainbow emergency number to inform the representative on duty about the incident.",
         emergencyText1:
            "We will be glad to answer all your questions during the welcome meeting and the hotel visits. However, if a matter is particularly urgent, the representative on duty is always at your disposal. You can reach them on the emergency number.",
         emergencyText2:
            "It may happen that the representative is unable to answer a call due to poor mobile coverage or because they are leading a local excursion. In that case, please contact our local office, where you can also get assistance.",
         meetingsText:
            "We warmly invite you to the meetings with your representative. The first one - the welcome meeting - covers practical information about the holiday destination and the organisation of your stay. During the week, the representative is also at your disposal at the hotel visits, according to the schedule below:"
      },
      flight: {
         headerBadge: "Return flight (to Poland)",
         title: "Departure points and times",
         transferInfo: "Transfer information for departures on:",
         importantInfo: "IMPORTANT INFORMATION! Please make sure you read it!",
         detailedInfo: "Detailed departure information",
         checkTransferTime:
            "Please check the airport transfer time before going to the pick-up point - last-minute changes are possible!",
         table: {
            departureTo: "Departure to",
            flightNumber: "Flight number",
            meetingLocalTime: "Pick-up (local time)",
            departureLocalTime: "Departure (local time)",
            arrivalPolishTime: "Arrival (Polish time)",
            baggageAllowance: "Baggage allowance",
            notes: "Remarks"
         }
      }
   }
};

export default en;
